import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import GoogleAuthPanel from "./components/GoogleAuthPanel";
import {
  persistAuthRedirectFromSearch,
  getAuthRedirectTarget,
  clearAuthRedirect,
  signupHrefWithRedirect,
} from "./utils/authRedirect";

export default function SignInPage({ isDarkMode }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";
  const redirectParam = searchParams.get("redirect") || "";

  useEffect(() => {
    persistAuthRedirectFromSearch(searchParams);
  }, [searchParams]);

  const handleGoogleCallback = useCallback(async (code) => {
    setLoading(true);
    setError("");
    try {
      const response = await axios.post("/api/auth/google/code/", { code });
      if (response.data.success) {
        localStorage.setItem("token", response.data.token);
        localStorage.setItem("userEmail", response.data.email);
        localStorage.setItem("userName", response.data.name);
        window.dispatchEvent(new Event("cliptranscript-auth-changed"));
        const dest = getAuthRedirectTarget(searchParams);
        clearAuthRedirect();
        navigate(dest);
      } else {
        setError("Google sign in failed");
      }
    } catch (err) {
      console.error("Google OAuth callback error:", err);
      setError("Google sign in failed. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [navigate, searchParams]);

  useEffect(() => {
    const code = searchParams.get("code");
    if (code) {
      handleGoogleCallback(code);
    }
  }, [searchParams, handleGoogleCallback]);

  const onGoogleSuccess = useCallback(() => {
    const dest = getAuthRedirectTarget(searchParams);
    clearAuthRedirect();
    navigate(dest);
  }, [navigate, searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("Email/password login is temporarily disabled. Please use Google.");
  };

  const signupHref = signupHrefWithRedirect(redirectParam);

  return (
    <div
      style={{
        padding: "24px 16px",
        maxWidth: "520px",
        margin: "0 auto",
        width: "100%",
        minHeight: "calc(100vh - 180px)",
        color: isDarkMode ? "#fff" : "#111827",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          width: "100%",
          background: isDarkMode ? "#111827" : "#ffffff",
          border: `1px solid ${isDarkMode ? "#374151" : "#e5e7eb"}`,
          borderRadius: "16px",
          padding: "24px",
          boxShadow: isDarkMode
            ? "0 10px 28px rgba(0,0,0,0.35)"
            : "0 10px 28px rgba(17,24,39,0.08)",
        }}
      >
        <h1 style={{ textAlign: "center", marginBottom: "8px", fontSize: "28px" }}>
          Welcome Back
        </h1>
        <p
          style={{
            textAlign: "center",
            marginBottom: "20px",
            color: isDarkMode ? "#9ca3af" : "#6b7280",
          }}
        >
          Continue with Google to sign in securely
        </p>

        {googleClientId && (
          <div style={{ marginBottom: "18px" }}>
            <GoogleAuthPanel
              buttonId="google-signin-button"
              variant="signin_with"
              isDarkMode={isDarkMode}
              onSuccess={onGoogleSuccess}
              onError={(msg) => setError(msg)}
            />
          </div>
        )}

        {!googleClientId && (
          <p style={{ color: "#ef4444", textAlign: "center", marginBottom: "18px" }}>
            Google Client ID is not configured.
          </p>
        )}

        {loading && (
          <p style={{ textAlign: "center", color: isDarkMode ? "#9ca3af" : "#6b7280" }}>
            Signing in...
          </p>
        )}

        <div
          style={{
            textAlign: "center",
            margin: "14px 0 18px",
            color: isDarkMode ? "#9ca3af" : "#6b7280",
            fontSize: "14px",
          }}
        >
          Or use email/password (coming soon)
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: "12px" }}>
            <label style={{ display: "block", marginBottom: "6px" }}>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled
              placeholder="you@example.com"
              style={{
                width: "100%",
                padding: "12px",
                border: `1px solid ${isDarkMode ? "#4b5563" : "#d1d5db"}`,
                borderRadius: "10px",
                backgroundColor: isDarkMode ? "#1f2937" : "#f9fafb",
                color: isDarkMode ? "#d1d5db" : "#6b7280",
                boxSizing: "border-box",
                opacity: 0.8,
              }}
            />
          </div>
          <div style={{ marginBottom: "14px" }}>
            <label style={{ display: "block", marginBottom: "6px" }}>Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled
              placeholder="********"
              style={{
                width: "100%",
                padding: "12px",
                border: `1px solid ${isDarkMode ? "#4b5563" : "#d1d5db"}`,
                borderRadius: "10px",
                backgroundColor: isDarkMode ? "#1f2937" : "#f9fafb",
                color: isDarkMode ? "#d1d5db" : "#6b7280",
                boxSizing: "border-box",
                opacity: 0.8,
              }}
            />
          </div>
          {error && <p style={{ color: "#ef4444", marginBottom: "12px" }}>{error}</p>}
          <button
            type="submit"
            disabled
            style={{
              width: "100%",
              padding: "12px",
              backgroundColor: "#9ca3af",
              color: "white",
              border: "none",
              borderRadius: "10px",
              cursor: "not-allowed",
              fontWeight: 600,
            }}
          >
            Email Sign In (Disabled)
          </button>
        </form>
        <p style={{ textAlign: "center", marginTop: "18px" }}>
          Don&apos;t have an account?{" "}
          <Link to={signupHref} style={{ color: "#2563eb", fontWeight: 600 }}>
            Sign Up
          </Link>
        </p>
      </div>
    </div>
  );
}
